import type { MovementType } from '../characters/types';
import { sightBlocked } from './combat';
import { hazardReaches } from './hazard';
import type { TileMap } from './tilemap';
import type { Fighter, Vec3 } from './types';

/** A horizontal unit direction. */
export interface Dir2 {
  x: number;
  z: number;
}

export interface PathMemory {
  path: Vec3[];
  pathAge: number;
  pathGoal: Vec3 | null;
}

/** Extra cost of stepping onto a tile, for planners that prefer (say) tiles out of the enemy's sight. */
export type TileCost = (x: number, y: number) => number;

/** What moving about needs to know about a fighter: where it is, how tall it is, what it may cross. */
export type Mover = Pick<Fighter, 'pos' | 'height' | 'movement'>;

/** A path is followed this long before a fresh one is planned regardless of anything else. */
const REPLAN_SECONDS = 2;
/** How soon to try again when there was no path. */
const RETRY_SECONDS = 0.5;
/** A target that moved this far from the goal of the current plan gets a new one at once. */
const REPLAN_DRIFT = 1.5;
/** A next waypoint further away than this means the fighter was pushed off its path. */
const OFF_TRACK = 2.5;
/** Tiles off the previous route cost this much extra, so the route only changes when clearly better. */
const NEW_ROUTE_COST = 0.35;
/** A* gives up after this many expansions so an unreachable goal cannot stall a tick. */
const MAX_EXPANSIONS = 4000;
/** How far out `nearestPassable` looks for somewhere to aim at when the goal itself is a wall. */
const GOAL_SEARCH_RADIUS = 12;
const WAYPOINT_REACHED = 0.35;
/** Fighters circle in these sixteen directions when they are not following a path. */
const COMPASS: readonly Dir2[] = Array.from({ length: 16 }, (_, i) => ({
  x: Math.sin((i * Math.PI) / 8),
  z: Math.cos((i * Math.PI) / 8),
}));

export function normalizeDir(x: number, z: number): Dir2 | null {
  const len = Math.hypot(x, z);
  return len > 1e-6 ? { x: x / len, z: z / len } : null;
}

export function flip(d: Dir2): Dir2 {
  return { x: -d.x, z: -d.z };
}

export function distanceXZ(a: Dir2, b: Dir2): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

/** The tile under a world position, when this movement class may cross it. */
export function standable(
  map: TileMap,
  p: { x: number; z: number },
  movement: MovementType,
): { x: number; y: number } | null {
  const x = Math.floor(p.x);
  const y = Math.floor(p.z);
  return map.inside(x, y) && map.passable(map.tile(x, y), movement) ? { x, y } : null;
}

/**
 * The nearest tile to `p` this movement class can cross, for goals that are themselves walls —
 * a fighter chasing something on the far side of a rock still wants to walk towards the rock.
 * Searched in rings so the first hit is the nearest.
 */
function nearestPassable(
  map: TileMap,
  p: { x: number; z: number },
  movement: MovementType,
): { x: number; y: number } | null {
  const here = standable(map, p, movement);
  if (here) return here;
  const cx = Math.floor(p.x);
  const cy = Math.floor(p.z);
  for (let r = 1; r <= GOAL_SEARCH_RADIUS; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = cx + dx;
        const y = cy + dy;
        if (map.inside(x, y) && map.passable(map.tile(x, y), movement)) return { x, y };
      }
    }
  }
  return null;
}

/** Feet position reached by moving `metres` along `dir`, or null when the tile there is no good. */
export function footholdAhead(map: TileMap, mover: Mover, dir: Dir2, metres: number): Vec3 | null {
  const x = mover.pos.x + dir.x * metres;
  const z = mover.pos.z + dir.z * metres;
  if (!standable(map, { x, z }, mover.movement)) return null;
  // A hazard is standable but not somewhere to walk of your own accord; a flier is over it.
  if (hazardReaches(map.tileAt(x, z), mover.movement)) return null;
  return { x, y: mover.pos.y, z };
}

/** How much room there is in a direction, 0…1: more when the next few metres are all standable. */
export function openness(map: TileMap, mover: Mover, dir: Dir2): number {
  let open = 0;
  for (const metres of [1.5, 2.5, 3.5]) if (footholdAhead(map, mover, dir, metres)) open++;
  return open / 3;
}

/**
 * The compass direction with the best score among those with ground to stand on 0.7 m and 1.5 m
 * ahead. `score` sees where the fighter would be after 1.5 m.
 */
export function bestDirection(
  map: TileMap,
  mover: Mover,
  score: (at: Vec3, dir: Dir2) => number,
): Dir2 | null {
  let best: Dir2 | null = null;
  let bestScore = -Infinity;
  for (const dir of COMPASS) {
    if (!footholdAhead(map, mover, dir, 0.7)) continue;
    const at = footholdAhead(map, mover, dir, 1.5);
    if (!at) continue;
    const value = score(at, dir);
    if (value > bestScore) {
      bestScore = value;
      best = dir;
    }
  }
  return best;
}

/**
 * The nearest standable tile within `radius` that the enemy's eye cannot see. Tiles that are
 * out of plain view from here are skipped: they are usually round the far side of the obstacle,
 * and the walk there would cross the enemy's line of fire.
 */
export function coverSpot(
  map: TileMap,
  me: Fighter,
  enemyEye: Vec3,
  radius: number,
  usable: (at: Vec3) => boolean,
): Vec3 | null {
  const r = Math.ceil(radius);
  const cx = Math.floor(me.pos.x);
  const cz = Math.floor(me.pos.z);
  const myCentre = { x: me.pos.x, y: me.pos.y + me.height / 2, z: me.pos.z };
  let best: Vec3 | null = null;
  let bestDistance = Infinity;
  for (let y = cz - r; y <= cz + r; y++) {
    for (let x = cx - r; x <= cx + r; x++) {
      const at = { x: x + 0.5, y: me.pos.y, z: y + 0.5 };
      const d = distanceXZ(at, me.pos);
      if (d > radius || d >= bestDistance) continue;
      if (!standable(map, at, me.movement)) continue;
      if (hazardReaches(map.tileAt(at.x, at.z), me.movement)) continue;
      if (!usable(at)) continue;
      const centre = { x: at.x, y: at.y + me.height / 2, z: at.z };
      if (!sightBlocked(map, enemyEye, centre)) continue;
      if (d > 0.75 && sightBlocked(map, myCentre, centre)) continue;
      best = at;
      bestDistance = d;
    }
  }
  return best;
}

/**
 * Direction towards the next waypoint of an A* path to the target. The plan is kept while it is
 * usable: replanning every tick made fighters flip between two routes of similar length.
 */
export function followPath(
  map: TileMap,
  mover: Mover,
  to: Vec3,
  memory: PathMemory,
  dt: number,
  cost?: TileCost,
): Dir2 | null {
  const from = mover.pos;
  memory.pathAge += dt;
  const next = memory.path[0];
  const drifted = !memory.pathGoal || distanceXZ(to, memory.pathGoal) > REPLAN_DRIFT;
  const stale = memory.pathAge >= (next ? REPLAN_SECONDS : RETRY_SECONDS);
  const offTrack = next !== undefined && distanceXZ(next, from) > OFF_TRACK;
  if (drifted || stale || offTrack) {
    memory.path = findPath(map, from, to, {
      movement: mover.movement,
      cost: preferPreviousRoute(map, memory.path, cost),
    });
    memory.pathAge = 0;
    memory.pathGoal = { ...to };
  }
  while (memory.path.length > 0) {
    const wp = memory.path[0]!;
    const d = distanceXZ(wp, from);
    if (d < WAYPOINT_REACHED) memory.path.shift();
    else return { x: (wp.x - from.x) / d, z: (wp.z - from.z) / d };
  }
  const dir = normalizeDir(to.x - from.x, to.z - from.z);
  return dir && distanceXZ(to, from) > 0.01 ? dir : null;
}

/** Makes tiles off the remaining previous route a little dearer, so a plan only changes for a clearly better one. */
function preferPreviousRoute(
  map: TileMap,
  previous: Vec3[],
  cost?: TileCost,
): TileCost | undefined {
  if (previous.length === 0) return cost;
  const onRoute = new Set(previous.map((wp) => map.index(Math.floor(wp.x), Math.floor(wp.z))));
  return (x, y) => (onRoute.has(map.index(x, y)) ? 0 : NEW_ROUTE_COST) + (cost?.(x, y) ?? 0);
}

/**
 * A* over the tiles a movement class can cross. Waypoints are tile centres on the fighter's own
 * plane; `movement` is what decides whether water, lava or a low wall is in the way.
 */
export function findPath(
  map: TileMap,
  from: Vec3,
  to: Vec3,
  options: { movement?: MovementType; cost?: TileCost } = {},
): Vec3[] {
  const movement = options.movement ?? 'ground';
  const cost = options.cost;
  const start = nearestPassable(map, from, movement);
  const goal = nearestPassable(map, to, movement);
  if (!start || !goal) return [];
  const key = (x: number, y: number) => map.index(x, y);
  const open = new Map<number, { x: number; y: number; g: number; f: number }>();
  const came = new Map<number, number>();
  const closed = new Set<number>();
  const h = (x: number, y: number) => Math.abs(x - goal.x) + Math.abs(y - goal.y);
  const startKey = key(start.x, start.y);
  // A binary heap by f, where the open list was scanned whole for every tile expanded: on a large
  // scroll arena with a walled-off goal that was thousands of expansions over a list hundreds long,
  // every quarter second (core-battle:bugs#7). Ties go to the tile first opened, as the scan's did:
  // the same route to the tile, and so the same duels.
  const heap = new OpenHeap();
  const order = new Map<number, number>();
  const push = (k: number, f: number) => {
    let first = order.get(k);
    if (first === undefined) {
      first = order.size;
      order.set(k, first);
    }
    heap.push(k, f, first);
  };
  open.set(startKey, { ...start, g: 0, f: h(start.x, start.y) });
  push(startKey, h(start.x, start.y));
  let expanded = 0;
  while (open.size > 0 && expanded < MAX_EXPANSIONS) {
    const entry = heap.pop();
    if (!entry) break;
    const best = open.get(entry.key);
    // An entry left behind by a better route to its tile, or by the tile's expansion.
    if (!best || best.f !== entry.f) continue;
    const bestKey = entry.key;
    open.delete(bestKey);
    closed.add(bestKey);
    expanded++;
    if (best.x === goal.x && best.y === goal.y) {
      const path: Vec3[] = [];
      let k = bestKey;
      while (k !== startKey) {
        path.push({ x: (k % map.width) + 0.5, y: from.y, z: Math.floor(k / map.width) + 0.5 });
        k = came.get(k)!;
      }
      return path.reverse();
    }
    for (const [ox, oy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const) {
      const nx = best.x + ox;
      const ny = best.y + oy;
      if (!map.inside(nx, ny)) continue;
      const tile = map.tile(nx, ny);
      if (!map.passable(tile, movement)) continue;
      const nk = key(nx, ny);
      if (closed.has(nk)) continue;
      // Walking through fire is a route of last resort, not a short cut; flying over it is not.
      const hazard = hazardReaches(tile, movement) ? 8 : 0;
      const g = best.g + 1 / (tile.speedScale ?? 1) + hazard + (cost?.(nx, ny) ?? 0);
      const existing = open.get(nk);
      if (!existing || g < existing.g) {
        const f = g + h(nx, ny);
        open.set(nk, { x: nx, y: ny, g, f });
        push(nk, f);
        came.set(nk, bestKey);
      }
    }
  }
  return [];
}

/** A* open list: the smallest f first, then the tile opened first. */
class OpenHeap {
  private readonly items: { key: number; f: number; order: number }[] = [];

  push(key: number, f: number, order: number): void {
    const items = this.items;
    items.push({ key, f, order });
    let i = items.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (!this.before(items[i]!, items[parent]!)) break;
      [items[i], items[parent]] = [items[parent]!, items[i]!];
      i = parent;
    }
  }

  pop(): { key: number; f: number; order: number } | undefined {
    const items = this.items;
    const top = items[0];
    const last = items.pop();
    if (items.length > 0 && last) {
      items[0] = last;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let m = i;
        if (l < items.length && this.before(items[l]!, items[m]!)) m = l;
        if (r < items.length && this.before(items[r]!, items[m]!)) m = r;
        if (m === i) break;
        [items[i], items[m]] = [items[m]!, items[i]!];
        i = m;
      }
    }
    return top;
  }

  private before(a: { f: number; order: number }, b: { f: number; order: number }): boolean {
    return a.f < b.f || (a.f === b.f && a.order < b.order);
  }
}
